import { AnalyticsAlertResponse } from '@/components/dsr-analytics/types';
import {
  AnalyticsAlertStatusType,
  AnalyticsAlertType,
  GetActiveAnalyticsAlertsQuery,
  GetActiveAnalyticsAlertsQueryVariables,
  UpdateAnalyticsAlertsStatusesMutation,
  UpdateAnalyticsAlertsStatusesMutationVariables,
} from '@/types';
import { BRONCO_SUPPLIER_UUID } from '@/mocks/uuids';
import { gql, useMutation } from '@apollo/client';
import { mockAlertsData } from '@/mocks/dsr-analytics/data';
import { partnerSupplier } from '@/utils/environment';
import { UPDATE_ANALYTICS_ALERTS_STATUSES_MUTATION } from './insights-hub/use-analytics-alerts-actions';
import { useEffect, useMemo, useState } from 'react';
import { useGetDynamicConfigValueForChat } from './use-dynamic-config-value';
import { useWrappedQuery } from './use-wrapped-query';

interface QueryVariables {
  chatUUID: string;
}

export const ACTIVE_ANALYTICS_ALERTS_QUERY = gql`
  query GetActiveAnalyticsAlerts($chatUUID: uuid!) {
    getAnalyticsAlerts(chat_id: $chatUUID, alert_statuses: ACTIVE) {
      alerts_json
    }
  }
`;

const transformAlertsData = (data: AnalyticsAlertResponse[]) => {
  // transform data into the format expected by the AnalyticsAlerts UI
  return data.reduce<Record<string, AnalyticsAlertResponse>>((acc, alert) => {
    // for grouped alerts, we need to use the first alert_uuid from the data array
    const alertUUID =
      alert.alert_type === AnalyticsAlertType.OverduePayment
        ? alert.data[0]?.alert_uuid
        : alert.alert_uuid;

    return alertUUID
      ? {
          ...acc,
          [alertUUID]: alert,
        }
      : acc;
  }, {});
};

const filterAlertsData = (
  data: AnalyticsAlertResponse[],
  alertUUIDs: string[],
) => {
  return data.filter((alert) => {
    if (alert.alert_type === AnalyticsAlertType.OverduePayment) {
      // filter out grouped alerts that have been dismissed
      alert.data = alert.data.filter(
        (alertData) => !alertUUIDs.includes(alertData.alert_uuid),
      );
      // if there are no alerts left, remove the alert from the list
      return alert.data.length > 0;
    }
    return !alertUUIDs.includes(alert.alert_uuid);
  });
};

export const getAlertUUIDs = (alert: AnalyticsAlertResponse) => {
  if (alert.alert_type === AnalyticsAlertType.OverduePayment) {
    return alert.data.map((item) => item.alert_uuid);
  }

  return [alert.alert_uuid];
};

export const useDemoSalesAlerts = (queryVariables: QueryVariables) => {
  const [demoData, setDemoData] = useState<AnalyticsAlertResponse[]>(
    mockAlertsData[queryVariables.chatUUID] ?? [],
  );
  useEffect(() => {
    setDemoData(mockAlertsData[queryVariables.chatUUID] ?? []);
  }, [queryVariables.chatUUID]);

  const alerts = useMemo(() => {
    if (demoData.length === 0) {
      return {};
    }

    return transformAlertsData(demoData);
  }, [demoData]);

  const updateAlertStatuses = async (
    alert: AnalyticsAlertResponse,
    alertStatus: AnalyticsAlertStatusType,
  ) => {
    const alertUUIDs = getAlertUUIDs(alert);

    if (alertStatus === AnalyticsAlertStatusType.Active) {
      const alertsToAdd = mockAlertsData[queryVariables.chatUUID].filter(
        (alert) => {
          if (alert.alert_type === AnalyticsAlertType.OverduePayment) {
            // find matching alert and add it back to the list
            alert.data = alert.data.filter((alertData) =>
              alertUUIDs.includes(alertData.alert_uuid),
            );
            // if there is a matching alert, add alert back to the list
            return alert.data.length > 0;
          }
          return alertUUIDs.includes(alert.alert_uuid);
        },
      );
      // add mock alerts back to the list
      setDemoData([...alertsToAdd, ...demoData]);
    } else {
      // remove mock alerts from the list
      setDemoData(filterAlertsData(demoData, alertUUIDs));
    }
  };

  return {
    alerts,
    data: demoData,
    error: false,
    loading: false,
    updateAlertStatuses,
  };
};

export const useLiveSalesAlerts = (queryVariables: QueryVariables) => {
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const analyticsAlertsConfig = getDynamicConfigValueForChat(
    queryVariables.chatUUID,
    'analytics_alerts_config',
  );

  const {
    loading,
    data: fetchedData,
    error,
  } = useWrappedQuery<
    GetActiveAnalyticsAlertsQuery,
    GetActiveAnalyticsAlertsQueryVariables
  >(ACTIVE_ANALYTICS_ALERTS_QUERY, {
    context: {
      noBatch: true,
    },
    skip: !analyticsAlertsConfig,
    variables: {
      chatUUID: queryVariables.chatUUID,
    },
  });
  const [updateAnalyticsAlerts] = useMutation<
    UpdateAnalyticsAlertsStatusesMutation,
    UpdateAnalyticsAlertsStatusesMutationVariables
  >(UPDATE_ANALYTICS_ALERTS_STATUSES_MUTATION);

  const data = useMemo(() => {
    if (loading || !fetchedData) {
      return;
    }

    if (fetchedData?.getAnalyticsAlerts) {
      const analyticsAlerts = fetchedData.getAnalyticsAlerts.alerts_json.map(
        (alert) => {
          return JSON.parse(alert);
        },
      );
      return analyticsAlerts;
    } else {
      return [];
    }
  }, [fetchedData, loading]);

  const alerts = useMemo(() => {
    if (!data) {
      return {};
    }

    return transformAlertsData(data);
  }, [data]);

  const updateAlertStatuses = async (
    alert: AnalyticsAlertResponse,
    alertStatus: AnalyticsAlertStatusType,
  ) => {
    const alertUUIDs = getAlertUUIDs(alert);

    await updateAnalyticsAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDs.length,
        },
      },
      update: (client, { data }) => {
        // Known issues: Since we provide a successful optimistic response and no rollback logic
        //   - if the BE returns an error, the cache will remain fully updated
        //   - if the BE returns a partial update, the cache will remain fully updated
        //   - this means we may have deleted alerts that are still active on the BE
        if (
          data?.updateAnalyticsAlertsStatuses.updatedCount === alertUUIDs.length
        ) {
          const existingData: GetActiveAnalyticsAlertsQuery | null =
            client.readQuery({
              query: ACTIVE_ANALYTICS_ALERTS_QUERY,
              variables: queryVariables,
            });

          if (existingData && existingData.getAnalyticsAlerts) {
            const existingAlerts =
              existingData.getAnalyticsAlerts.alerts_json.map((alert) =>
                JSON.parse(alert),
              );
            const updatedAlerts =
              alertStatus === AnalyticsAlertStatusType.Active
                ? [alert, ...existingAlerts]
                : filterAlertsData(existingAlerts, alertUUIDs);

            client.writeQuery<
              GetActiveAnalyticsAlertsQuery,
              GetActiveAnalyticsAlertsQueryVariables
            >({
              data: {
                ...existingData,
                getAnalyticsAlerts: {
                  ...existingData.getAnalyticsAlerts,
                  alerts_json: updatedAlerts.map((alert) =>
                    JSON.stringify(alert),
                  ),
                },
              },
              query: ACTIVE_ANALYTICS_ALERTS_QUERY,
              variables: queryVariables,
            });
          }
        }
      },
      variables: {
        alertStatus,
        alertUUIDs: alertUUIDs,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  return { alerts, error, loading, updateAlertStatuses };
};

export const useSalesAlerts = (
  queryVariables: QueryVariables,
  supplierUUID: string,
) => {
  const { alerts, updateAlertStatuses, error, loading } =
    useLiveSalesAlerts(queryVariables);
  const { alerts: demoAlerts, updateAlertStatuses: demoUpdateAlertStatuses } =
    useDemoSalesAlerts(queryVariables);

  // In order for BD to demo sales AI alerts to prospective customers, we need to display hardcoded alerts.
  // Show demo alerts when demos are enabled on the supplier and there are demo alerts to show.
  const shouldShowMockData =
    supplierUUID === BRONCO_SUPPLIER_UUID && Object.keys(demoAlerts).length > 0;

  return {
    alerts: shouldShowMockData ? demoAlerts : alerts,
    error,
    loading: shouldShowMockData ? false : loading,
    updateAlertStatuses: shouldShowMockData
      ? demoUpdateAlertStatuses
      : updateAlertStatuses,
  };
};
